const onDocumentReady = ( callback ) => {
    if ( document.readyState === 'loading' ) {
        document.addEventListener( 'DOMContentLoaded', callback, { once: true } );
    } else {
        callback();
    }
};

const getDotLabel = ( index, label ) => {
    if ( typeof label === 'function' ) {
        return label( index );
    }

    const base = typeof label === 'string' ? label : 'Slide';
    return `${ base } ${ index + 1 }`;
};

const initStackedSlider = ( slider, {
    slideSelector = '.stacked-slider__slide',
    dotsContainerSelector = '.stacked-slider__dots',
    prevArrowSelector = '.stacked-slider__arrow--prev',
    nextArrowSelector = '.stacked-slider__arrow--next',
    dotAriaLabel = undefined,
    onSlideChange = undefined,
} = {} ) => {
    if ( ! slider ) {
        return;
    }

    const slides = slider.querySelectorAll( slideSelector );
    if ( ! slides.length ) {
        return;
    }

    const dotsContainer = dotsContainerSelector ? slider.querySelector( dotsContainerSelector ) : null;
    const prevArrow = prevArrowSelector ? slider.querySelector( prevArrowSelector ) : null;
    const nextArrow = nextArrowSelector ? slider.querySelector( nextArrowSelector ) : null;
    let dots = [];

    if ( dotsContainer ) {
        dotsContainer.innerHTML = '';

        slides.forEach( ( _slide, index ) => {
            const dot = document.createElement( 'button' );
            dot.type = 'button';
            dot.setAttribute( 'aria-label', getDotLabel( index, dotAriaLabel ) );
            dot.addEventListener( 'click', () => goToSlide( index ) );
            dotsContainer.appendChild( dot );
        } );

        dots = Array.from( dotsContainer.querySelectorAll( 'button' ) );
    }
    const autoplay = slider.dataset.autoplay === '1';
    const parsedInterval = parseInt( slider.dataset.interval, 10 );
    const interval = Number.isFinite( parsedInterval ) ? parsedInterval : 7000;
    let current = 0;
    let timer = null;

    const updateSlides = () => {
        slides.forEach( ( slide ) => {
            slide.classList.remove( 'active', 'prev', 'next', 'next-2' );
        } );

        dots.forEach( ( dot ) => dot.classList.remove( 'active' ) );

        const prevIndex = ( current - 1 + slides.length ) % slides.length;
        const nextIndex = ( current + 1 ) % slides.length;
        const next2Index = ( current + 2 ) % slides.length;

        slides[ current ].classList.add( 'active' );
        slides[ prevIndex ].classList.add( 'prev' );
        slides[ nextIndex ].classList.add( 'next' );
        slides[ next2Index ].classList.add( 'next-2' );

        if ( dots[ current ] ) {
            dots[ current ].classList.add( 'active' );
        }

        const detail = {
            currentIndex: current,
            currentSlide: slides[ current ],
            slides,
            slider,
        };

        if ( typeof onSlideChange === 'function' ) {
            onSlideChange( detail );
        }

        slider.dispatchEvent( new CustomEvent( 'stackedSliderChange', { detail } ) );
    };

    const goToSlide = ( index ) => {
        current = index;
        updateSlides();
        if ( autoplay ) {
            restartAutoplay();
        }
    };

    const goToPrev = () => {
        const prev = ( current - 1 + slides.length ) % slides.length;
        goToSlide( prev );
    };

    const goToNext = () => {
        const next = ( current + 1 ) % slides.length;
        goToSlide( next );
    };

    const toggleArrowState = () => {
        const disabled = slides.length <= 1;

        [ prevArrow, nextArrow ].forEach( ( arrow ) => {
            if ( ! arrow ) {
                return;
            }

            arrow.disabled = disabled;
            arrow.setAttribute( 'aria-disabled', disabled ? 'true' : 'false' );
        } );
    };

    const restartAutoplay = () => {
        if ( ! autoplay || slides.length <= 1 ) {
            return;
        }

        clearInterval( timer );
        timer = setInterval( () => {
            const next = ( current + 1 ) % slides.length;
            goToSlide( next );
        }, interval );
    };

    slider.addEventListener( 'mouseenter', () => {
        if ( autoplay ) {
            clearInterval( timer );
        }
    } );

    slider.addEventListener( 'mouseleave', () => {
        restartAutoplay();
    } );

    if ( prevArrow ) {
        prevArrow.addEventListener( 'click', goToPrev );
    }

    if ( nextArrow ) {
        nextArrow.addEventListener( 'click', goToNext );
    }

    goToSlide( 0 );
    toggleArrowState();
    restartAutoplay();
};

const initStackedSliders = ( selector, options ) => {
    document.querySelectorAll( selector ).forEach( ( slider ) => {
        initStackedSlider( slider, options );
    } );
};

const setupStackedSliderHeight = ( slider, {
    wrapperSelector,
    slideSelector,
} = {} ) => {
    if ( ! slider || ! wrapperSelector || ! slideSelector ) {
        return null;
    }

    const wrapper = slider.querySelector( wrapperSelector );

    if ( ! wrapper ) {
        return null;
    }

    let raf = null;
    let resizeObserver = null;

    const getActiveSlide = () =>
        slider.querySelector( `${ slideSelector }.active` ) || slider.querySelector( slideSelector );

    const setHeight = () => {
        const activeSlide = getActiveSlide();

        if ( ! activeSlide ) {
            return;
        }

        const slideHeight = activeSlide.scrollHeight || activeSlide.offsetHeight;

        if ( slideHeight ) {
            wrapper.style.height = `${ slideHeight }px`;
        }
    };

    const schedule = () => {
        if ( raf ) {
            window.cancelAnimationFrame( raf );
        }

        raf = window.requestAnimationFrame( () => {
            raf = null;
            setHeight();
        } );
    };

    const observe = ( slide ) => {
        if ( ! window.ResizeObserver || ! slide ) {
            return;
        }

        if ( resizeObserver ) {
            resizeObserver.disconnect();
        }

        resizeObserver = new ResizeObserver( () => schedule() );
        resizeObserver.observe( slide );
    };

    const handleResize = () => schedule();

    window.addEventListener( 'resize', handleResize );

    slider.querySelectorAll( 'img' ).forEach( ( image ) => {
        if ( image.dataset.lsStackedSliderBound === '1' ) {
            return;
        }

        image.dataset.lsStackedSliderBound = '1';

        if ( image.complete ) {
            return;
        }

        image.addEventListener( 'load', schedule );
        image.addEventListener( 'error', schedule );
    } );

    schedule();

    return {
        schedule,
        observe,
        destroy: () => {
            if ( raf ) {
                window.cancelAnimationFrame( raf );
            }

            if ( resizeObserver ) {
                resizeObserver.disconnect();
            }

            window.removeEventListener( 'resize', handleResize );
        },
    };
};

export { onDocumentReady, initStackedSlider, initStackedSliders, setupStackedSliderHeight };
